import React from 'react';
import { Loader2, RefreshCw, Search, Trash2, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AdminMediaTab = ({
  mediaFiles,
  mediaFilter,
  setMediaFilter,
  mediaSearch,
  setMediaSearch,
  loadingMedia,
  fetchMedia,
  handleScanBroken,
  handleCleanupOrphaned,
  handleDeleteMedia,
  orphanedCount,
  API_URL
}) => {
  const filteredMedia = mediaFiles
    .filter(file => {
      // Apply type filter
      if (mediaFilter === 'logo' && file.used_as !== 'logo') return false;
      if (mediaFilter === 'banner' && file.used_as !== 'banner') return false;
      if (mediaFilter === 'gallery' && file.used_as !== 'gallery') return false;
      if (mediaFilter === 'unused' && (file.stall_name || file.storage === 'orphaned' || file.exists === false)) return false;
      if (mediaFilter === 'orphaned' && file.storage !== 'orphaned' && file.exists !== false) return false;
      // 'all' shows everything
      
      // Apply search filter
      if (mediaSearch) {
        const search = mediaSearch.toLowerCase();
        return file.filename.toLowerCase().includes(search) ||
          (file.stall_name && file.stall_name.toLowerCase().includes(search));
      }
      return true;
    });

  const filterTabs = [
    { key: 'all', label: 'All', count: mediaFiles.length },
    { key: 'logo', label: 'Logos', count: mediaFiles.filter(f => f.used_as === 'logo').length },
    { key: 'banner', label: 'Banners', count: mediaFiles.filter(f => f.used_as === 'banner').length },
    { key: 'gallery', label: 'Gallery', count: mediaFiles.filter(f => f.used_as === 'gallery').length },
    { key: 'unused', label: 'Unused', count: mediaFiles.filter(f => !f.stall_name && f.storage !== 'orphaned' && f.exists !== false).length },
    { key: 'orphaned', label: 'Broken', count: mediaFiles.filter(f => f.storage === 'orphaned' || f.exists === false).length }
  ];

  return (
    <div className="space-y-4 mt-4">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-3">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setMediaFilter(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              mediaFilter === tab.key
                ? 'bg-[#FFD12D] text-[#252525]'
                : tab.key === 'orphaned' && tab.count > 0
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200'
                  : tab.key === 'unused' && tab.count > 0
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button
          onClick={fetchMedia}
          disabled={loadingMedia}
          size="sm"
          className="bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] text-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingMedia ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button
          onClick={handleScanBroken}
          size="sm"
          variant="outline"
          className="text-sm"
        >
          <Search className="w-4 h-4 mr-2" />
          Scan for Broken
        </Button>
        {orphanedCount > 0 && (
          <Button
            onClick={handleCleanupOrphaned}
            variant="destructive"
            size="sm"
            className="text-sm"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clean Up {orphanedCount} Broken
          </Button>
        )}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search media files..."
            value={mediaSearch}
            onChange={(e) => setMediaSearch(e.target.value)}
            className="pl-9 h-9 dark:bg-[#1a1a1a] dark:text-white"
          />
        </div>
      </div>

      {/* Media Grid */}
      {loadingMedia ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FFD12D]" />
          <p className="mt-2 text-gray-500">Loading media files...</p>
        </div>
      ) : mediaFiles.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No media files uploaded yet</p>
          <p className="text-sm mt-2">Upload images via the Stalls tab or Vendor Dashboard</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredMedia.map((file) => (
            <div 
              key={file.filename}
              className={`relative group rounded-lg overflow-hidden border-2 transition-colors ${
                file.exists === false 
                  ? 'border-red-400 bg-red-50 dark:bg-red-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#FFD12D]'
              }`}
            >
              <div className="aspect-square bg-gray-100 dark:bg-gray-800">
                {file.exists === false ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-red-500 p-2">
                    <AlertTriangle className="w-8 h-8 mb-2" />
                    <span className="text-xs text-center">File Missing</span>
                    <span className="text-xs text-gray-500 mt-1">{file.stall_name}</span>
                  </div>
                ) : (
                  <>
                    <img
                      src={`${API_URL}${file.file_path}`}
                      alt={file.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full items-center justify-center text-gray-400 text-xs hidden">
                      Failed to load
                    </div>
                  </>
                )}
              </div>
              
              {/* Type badge */}
              {file.used_as && file.exists !== false && (
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium ${
                  file.used_as === 'logo' 
                    ? 'bg-blue-500 text-white' 
                    : file.used_as === 'banner'
                      ? 'bg-purple-500 text-white'
                      : 'bg-emerald-500 text-white'
                }`}>
                  {file.used_as}
                </div>
              )}
              
              {/* Overlay with info */}
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity p-2 flex flex-col justify-between">
                <div className="text-white text-xs">
                  <p className="truncate font-medium">{file.filename}</p>
                  {file.exists !== false && <p className="text-gray-300">{file.size_kb} KB</p>}
                  {file.stall_name && (
                    <p className="text-[#FFD12D] mt-1">{file.stall_name}</p>
                  )}
                  {!file.stall_name && file.exists !== false && (
                    <p className="text-red-400 mt-1">Unused</p>
                  )}
                  {file.exists === false && (
                    <p className="text-red-400 mt-1">Orphaned reference</p>
                  )}
                </div>
                
                <div className="flex gap-1">
                  {file.exists !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-7 text-xs bg-white/10 border-white/30 text-white hover:bg-white/20"
                      onClick={() => window.open(`${API_URL}${file.file_path}`, '_blank')}
                    >
                      View
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 w-7 p-0"
                    onClick={() => handleDeleteMedia(file.filename)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              
              {/* Status indicator */}
              {!file.stall_name && (
                <div className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full" title="Unused file"></div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMediaTab;
